import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut } from "lucide-react";
import toast from "react-hot-toast";
import { logout } from "@/services/auth";
import { setLoggingOut } from "@/lib/axiosClient";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from 'react-i18next';

type AuthButtonProps = {
    checkAuth?: boolean;
    isPublic?: boolean;
    iconOnly?: boolean;
};

const AuthButton: React.FC<AuthButtonProps> = ({
    checkAuth = true,
    isPublic = false,
    iconOnly = false,
}) => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user, setUser, loading: authLoading } = useAuth();

    const { t } = useTranslation('common');

    const isLoggedIn = !!user;

    const handleLoginClick = () => {
        if (isLoggedIn) {
            navigate("/dashboard");
        } else {
            navigate("/login");
        }
    };

    const handleLogout = async () => {
        setLoading(true);
        setLoggingOut(true);

        // 1. Bersihkan Tab Ini
        toast.success("Logout berhasil");
        setUser(null);
        queryClient.removeQueries();
        queryClient.clear();
        navigate("/login", { replace: true });

        // 2. KIRIM SINYAL KE TAB LAIN (Via Memori)
        // Tanpa menyentuh LocalStorage sama sekali
        const channel = new BroadcastChannel("auth_channel");
        channel.postMessage("LOGOUT_SIGNAL");
        channel.close();

        // 3. Request Server (Hapus Cookie)
        try {
            await logout();
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            setLoggingOut(false);
        }
    };

    if (checkAuth && authLoading) {
        return (
            <Button variant="ghost" disabled className="h-9">
                {iconOnly ? (
                    <span className="relative flex h-5 w-5 items-center justify-center">
                        <span className="absolute h-5 w-5 border-2 border-t-transparent border-slate-500 rounded-full animate-spin" />
                    </span>
                ) : (
                    "..."
                )}
            </Button>
        );
    }

    if (isPublic) {
        return (
            <Button
                onClick={() => navigate("/login")}
                variant="ghost"
                className="h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900
                dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white
                transition-colors"
            >
                <LogIn className="h-5 w-5" />
                {!iconOnly && <span className="ml-2">{t('buttons.login')}</span>}
            </Button>
        );
    }

    return isLoggedIn ? (
        <Button
            onClick={handleLogout}
            disabled={loading}
            variant="ghost"
            className="h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900
            dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white
            transition-colors"
        >
            {loading ? (
                <span className="relative flex h-5 w-5 items-center justify-center">
                    <span className="absolute h-5 w-5 border-2 border-t-transparent border-slate-500 rounded-full animate-spin" />
                </span>
            ) : (
                <LogOut className="h-5 w-5" />
            )}
            {!iconOnly && (
                <span className="ml-2">{loading ? "Keluar..." : "Logout"}</span>
            )}
        </Button>
    ) : (
        <Button
            onClick={handleLoginClick}
            variant="ghost"
            className="h-9 text-slate-700 hover:bg-slate-100 hover:text-slate-900
            dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white
            transition-colors"
        >
            <LogIn className="h-5 w-5" />
            {!iconOnly && <span className="ml-2">{t('buttons.login')}</span>}
        </Button>
    );
};

export default AuthButton;